import { GRID_SIZE, TileType, pointKey, type Point } from "./types";

const WALKABLE_TILES = new Set<TileType>([
  TileType.Floor,
  TileType.Toxic,
  TileType.Door,
  TileType.SecretDoor,
]);

export class GridMap {
  private readonly cells: Uint8Array;
  private readonly blockedEdgeKeys = new Set<string>();

  public constructor(
    public readonly width = GRID_SIZE,
    public readonly height = GRID_SIZE,
  ) {
    if (width <= 0 || height <= 0) {
      throw new RangeError("Grid dimensions must be positive");
    }
    this.cells = new Uint8Array(width * height);
    this.cells.fill(TileType.Wall);
  }

  public inBounds(point: Point): boolean {
    return point.x >= 0 && point.y >= 0 && point.x < this.width && point.y < this.height;
  }

  public get(point: Point): TileType {
    if (!this.inBounds(point)) {
      return TileType.Wall;
    }
    return this.cells[this.indexOf(point)] as TileType;
  }

  public set(point: Point, tile: TileType): void {
    if (!this.inBounds(point)) {
      throw new RangeError(`Grid coordinate outside map: ${pointKey(point)}`);
    }
    this.cells[this.indexOf(point)] = tile;
  }

  public fillRect(x: number, y: number, width: number, height: number, tile: TileType): void {
    for (let cellY = y; cellY < y + height; cellY += 1) {
      for (let cellX = x; cellX < x + width; cellX += 1) {
        this.set({ x: cellX, y: cellY }, tile);
      }
    }
  }

  public isWalkable(point: Point, includeClosedDoors = true): boolean {
    const tile = this.get(point);
    if (!includeClosedDoors && (tile === TileType.Door || tile === TileType.SecretDoor)) {
      return false;
    }
    return WALKABLE_TILES.has(tile);
  }

  public neighbors4(point: Point): Point[] {
    const candidates: Point[] = [
      { x: point.x + 1, y: point.y },
      { x: point.x - 1, y: point.y },
      { x: point.x, y: point.y + 1 },
      { x: point.x, y: point.y - 1 },
    ];
    return candidates.filter((candidate) => this.inBounds(candidate));
  }

  public blockEdge(first: Point, second: Point): void {
    this.assertAdjacent(first, second);
    this.blockedEdgeKeys.add(edgeKey(first, second));
  }

  public isEdgeBlocked(first: Point, second: Point): boolean {
    if (!this.inBounds(first) || !this.inBounds(second)) return true;
    return this.blockedEdgeKeys.has(edgeKey(first, second));
  }

  public canTraverse(first: Point, second: Point, includeClosedDoors = true): boolean {
    return (
      this.inBounds(first) &&
      this.inBounds(second) &&
      Math.abs(first.x - second.x) + Math.abs(first.y - second.y) === 1 &&
      !this.isEdgeBlocked(first, second) &&
      this.isWalkable(second, includeClosedDoors)
    );
  }

  public floodFill(start: Point, includeClosedDoors = true): Set<string> {
    if (!this.isWalkable(start, includeClosedDoors)) {
      return new Set<string>();
    }

    const visited = new Set<string>([pointKey(start)]);
    const queue: Point[] = [start];
    let cursor = 0;
    while (cursor < queue.length) {
      const current = queue[cursor] as Point;
      cursor += 1;
      for (const neighbor of this.neighbors4(current)) {
        const key = pointKey(neighbor);
        if (
          !visited.has(key) &&
          this.canTraverse(current, neighbor, includeClosedDoors)
        ) {
          visited.add(key);
          queue.push(neighbor);
        }
      }
    }
    return visited;
  }

  public walkableCells(): Point[] {
    const cells: Point[] = [];
    for (let y = 0; y < this.height; y += 1) {
      for (let x = 0; x < this.width; x += 1) {
        const point = { x, y };
        if (this.isWalkable(point)) {
          cells.push(point);
        }
      }
    }
    return cells;
  }

  public serialize(): string {
    return `${Array.from(this.cells).join("")}|${[...this.blockedEdgeKeys].sort().join(";")}`;
  }

  private indexOf(point: Point): number {
    return point.y * this.width + point.x;
  }

  private assertAdjacent(first: Point, second: Point): void {
    if (
      !this.inBounds(first) ||
      !this.inBounds(second) ||
      Math.abs(first.x - second.x) + Math.abs(first.y - second.y) !== 1
    ) {
      throw new RangeError(`Grid edge must connect adjacent cells: ${pointKey(first)} -> ${pointKey(second)}`);
    }
  }
}

function edgeKey(first: Point, second: Point): string {
  const firstKey = pointKey(first);
  const secondKey = pointKey(second);
  return firstKey < secondKey ? `${firstKey}|${secondKey}` : `${secondKey}|${firstKey}`;
}
